/**
 * Image cropper: re-written to functional react
 * https://github.com/ricardo-ch/react-easy-crop
 * https://codesandbox.io/s/y09komm059
 */
import { Button, CircularProgress, Slider } from '@mui/material';
import loadImage from 'blueimp-load-image';
import React, { createRef, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import { convertHeicIfNeeded } from 'utils/heic-image';
import { createImage, getCroppedImg } from 'utils/image-file';
import captureException from 'utils/sentry';
import { useNotification } from 'utils/snackbar';

type Props = {
  file: File;
  onSuccess: (file: Blob) => void;
  onError?: (err: Error) => void;
  isLoading?: (boolean: boolean) => void;
  isCropping?: (boolean: boolean) => void;
};

const ASPECT = 4 / 4;
const CROP_BOX_OFFSET = 50;

const ImageCrop = (props: Props) => {
  const { file, onSuccess, onError, isLoading, isCropping } = props;
  const { showErrorNotification } = useNotification();
  const { t } = useTranslation();
  const cropContainerRef = createRef<HTMLDivElement>();

  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cropping, setCropping] = useState(false);
  const [minZoom, setMinZoom] = useState(1);
  const [cropSize, setCropSize] = useState({ width: 250, height: 250 });

  const isLoadingCallback = (loadInProgress: boolean) => {
    setLoading(loadInProgress);
    if (typeof isLoading === 'function') {
      isLoading(loadInProgress);
    }
  };

  const isCroppingCallback = (cropInProgress: boolean) => {
    setCropping(cropInProgress);
    if (typeof isCropping === 'function') {
      isCropping(cropInProgress);
    }
  };

  const errorCallback = (error: Error) => {
    if (typeof onError === 'function') {
      onError(error);
    }
  };

  const showResult = async () => {
    try {
      isCroppingCallback(true);

      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);

      isCroppingCallback(false);
      onSuccess(croppedImage);
    } catch (error) {
      showErrorNotification({ message: t('components.crop.error'), error });
      isCroppingCallback(false);
      errorCallback(error);
    }
  };

  useEffect(() => {
    (async () => {
      isLoadingCallback(true);

      if (file) {
        try {
          // Convert HEIC/HEIF (iPhone photos) to JPEG before blueimp-load-image, whose
          // <img>/canvas decoder can't read HEIC outside Safari. No-op for other formats.
          const safeFile = await convertHeicIfNeeded(file, (error) =>
            showErrorNotification({ message: t('components.product-edit.heic-conversion-error'), error: error as Error }),
          );

          const { image } = await loadImage(safeFile, {
            maxWidth: 2000,
            maxHeight: 2000,
            canvas: true,
            orientation: true,
            meta: true,
          });

          const imageDataUrl = image.toDataURL('image/jpeg');

          setZoom(zoom);
          setImageSrc(imageDataUrl);
          setCrop(crop);
        } catch (error) {
          showErrorNotification({
            message: t('components.crop.load-error'),
            error,
          });
          captureException(error, 'Failed uploading image');
          onError?.(error);
        }
      }

      isLoadingCallback(false);
    })();
  }, [file]);

  // Set responsive crop size and zoom
  useEffect(() => {
    const cropContainerEl = cropContainerRef?.current;

    const updateCropperSize = async () => {
      if (imageSrc && cropContainerEl) {
        const imageFile: any = await createImage(imageSrc);

        const newMinZoom = calculateMinZoom(
          imageFile.width,
          imageFile.height,
          cropContainerEl.offsetWidth,
          cropContainerEl.offsetHeight,
          CROP_BOX_OFFSET,
        );

        // Since the cropper size is strictly set, we need to calculate min zoom ourselves.
        setCropSize({
          width: cropContainerEl.offsetWidth - CROP_BOX_OFFSET,
          height: cropContainerEl.offsetHeight - CROP_BOX_OFFSET,
        });
        setMinZoom(newMinZoom);
        setZoom(newMinZoom);
      }
    };

    // Initial call
    updateCropperSize();

    // Update on resize
    window.addEventListener('resize', updateCropperSize);

    // Remove event listener when component destroyed
    return function cleanup() {
      window.removeEventListener('resize', updateCropperSize);
    };
  }, [imageSrc, cropContainerRef?.current]);

  return (
    imageSrc && (
      <div className="image-cropper">
        <div className="crop-container" ref={cropContainerRef}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={ASPECT}
            onCropChange={setCrop}
            onCropComplete={(_, newCroppedAreaPixels) => setCroppedAreaPixels(newCroppedAreaPixels)}
            onZoomChange={setZoom}
            showGrid={false}
            cropSize={cropSize}
            minZoom={minZoom}
            style={{
              cropAreaStyle: {
                border: '2px solid rgba(255,255,255,1)',
              },
            }}
          />
        </div>

        <div className="controls">
          <Slider
            value={zoom}
            min={minZoom}
            max={3}
            step={0.1}
            aria-labelledby="Zoom"
            onChange={(_, newZoom: unknown) => setZoom(newZoom as number)}
          />
        </div>

        <div className="actions">
          <Button
            className="crop-btn"
            color="primary"
            variant="contained"
            onClick={showResult}
            disabled={cropping || loading}
            startIcon={cropping || loading ? <CircularProgress size={24} className="load-icon" /> : null}
          >
            {t('components.crop.confirm')}
          </Button>
        </div>
      </div>
    )
  );
};

/**
 * Calculates min zoom for cropper that uses strictly set crop size
 * @param imgWidth
 * @param imgHeight
 * @param containerWidth
 * @param containerHeight
 * @param cropBoxOffset
 */
function calculateMinZoom(imgWidth: number, imgHeight: number, containerWidth: number, containerHeight: number, cropBoxOffset: number) {
  const maxContainerSize = containerWidth > containerHeight ? containerWidth : containerHeight;

  const scaledZoom = imgWidth > imgHeight ? imgWidth / imgHeight : imgHeight / imgWidth;

  return (scaledZoom / maxContainerSize) * (maxContainerSize - cropBoxOffset);
}

export default ImageCrop;
